// src/components/UpdateBoundary.tsx
import React from 'react';
import { CContainer, CCard, CCardBody, CCardTitle, CCardText, CButton, CRow, CCol } from '@coreui/react';
import { ConsoleLogger } from '@cloudflare-monorepo/utils/logging/logger.ts';

const logger = new ConsoleLogger('update-boundary');

function isVersionSkewError(err: unknown) {
  const e = err as any;
  const name = e?.name ?? '';
  const msg = (e?.message ?? '').toString();

  return (
    name === 'ChunkLoadError' || // webpack
    /Loading chunk \d+ failed/i.test(msg) || // webpack
    /Failed to fetch dynamically imported module/i.test(msg) || // Vite/ESM
    /Importing a module script failed/i.test(msg) // Safari/ESM
  );
}

type State = { hasError: boolean; error?: Error };

export class UpdateBoundary extends React.Component<React.PropsWithChildren, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return isVersionSkewError(error) ? { hasError: true, error } : { hasError: false, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    if (!isVersionSkewError(error)) logger.error(error.message || 'component render error', { error, info });
  }

  private reload = async (deep = false) => {
    logger.info('preparing to reload after failed chunk loading');

    try {
      sessionStorage.setItem('reloaded-for-chunk-error', '1');

      if (deep) {
        // Best-effort cleanup; bounded to this origin/scope.
        if ('serviceWorker' in navigator) {
          const regs = await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map((r) => r.unregister()));
        }
        if ('caches' in window) {
          const keys = await caches.keys();
          await Promise.all(keys.map((k) => caches.delete(k)));
        }
      }
    } catch (err) {
      // ignore
      logger.warn('failed to do best-effor cleanup before reloading', { error: err });
    } finally {
      // Hard replace avoids bfcache and history cruft
      logger.info('reloading after failed chunk loading');
      window.location.replace(window.location.pathname + window.location.search);
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <CContainer fluid className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
        <CRow className="w-100 justify-content-center">
          <CCol xs={12} sm={10} md={8} lg={6} xl={5}>
            <CCard className="shadow-lg rounded-4 border-0">
              <CCardBody className="text-center p-4">
                <CCardTitle className="h3 mb-3 text-dark">Questa applicazione è stata aggiornata!</CCardTitle>
                <CCardText className="text-muted mb-4">
                  È disponibile una nuova versione di questa applicazione.
                  <br />
                  Per continuare è necessario ricaricare la pagina.
                </CCardText>
                <div className="d-flex gap-3 justify-content-center">
                  {/*
                  <CButton
                    color="secondary"
                    variant="outline"
                    className="px-4"
                    onClick={() => this.reload(false)}
                  >
                    Ricarica pagina
                  </CButton>
                  */}
                  <CButton color="dark" className="px-4" onClick={() => this.reload(true)}>
                    Ricarica
                  </CButton>
                </div>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CContainer>
    );
  }
}
